import { Component, OnDestroy, OnInit } from '@angular/core';
import { RecipientCriptoService } from '../../services/recipient-cripto.service';
import { TransferenciasUSDT } from '../../interfaces/transferencia-usdt.interface';
import { MatDialog } from '@angular/material/dialog';
import { RejectTransactionModalComponent } from '../assigned-transactions/reject-transaction-modal/reject-transaction-modal.component';
import { Clipboard } from '@angular/cdk/clipboard';
import Swal from 'sweetalert2';
import { EditCriptoTransactionComponent } from './edit-cripto-transaction/edit-cripto-transaction.component';
import { SendTransactionaCriptoModalComponent } from './send-transactiona-cripto-modal/send-transactiona-cripto-modal.component';
import { CurrencyPipe } from '@angular/common';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { TransaccionService } from '../../services/transaccion.service';
import { BinanceWithdrawalsAdminService } from '../../services/binance-withdrawals-admin.service';
import { MediosCriptoService } from '../../services/medios-cripto.service';
import * as moment from 'moment-timezone';
import { firstValueFrom } from 'rxjs';
import {
  esRedBinance,
  etiquetaCortaRed,
  etiquetaRed,
  REDES_CRIPTO,
  redActiva,
  unirNombres,
} from 'src/app/shared/utils/red-cripto';

export type CriptoTab = 'CL' | 'VES';

@Component({
  selector: 'app-assigned-transactions-cripto',
  templateUrl: './assigned-transactions-cripto.component.html',
  styleUrls: ['./assigned-transactions-cripto.component.sass']
})
export class AssignedTransactionsCriptoComponent implements OnInit, OnDestroy {

  public transactionCripto: TransferenciasUSDT[] = [];
  displayedColumns: string[] = [
    'idTransferencia', 'fechaInicio', 'fechaFondoWallet', 'fechaTermino',
    'user', 'remitente', 'beneficiario', 'tn', 'montoCLP', 'montoUSDT',
    'walletTRC20', 'estadoActual', 'acciones'
  ];
  filteredTransactionCripto: TransferenciasUSDT[] = [];
  backTransactionCripto: TransferenciasUSDT[] = [];
  filterValue: string = '';
  public currentPage = 1;
  public pageSize = 20;
  public totalItems = 0;
  public totalPages = 0;

  // Tab actual: CL = transacciones cripto Chile, VES = retiros USDT de usuarios VE
  activeTab: CriptoTab = 'CL';

  /**
   * Filtro por red del beneficiario (pestaña Chile): `null` = todas. Va al
   * backend (`medioCriptoId`) y no se filtra la página en el front, para que
   * el paginador cuente lo filtrado.
   */
  filtroRed: number | null = null;
  readonly redesFiltro = REDES_CRIPTO;

  /**
   * "Solo por procesar" (las dos pestañas): pide al backend solo lo que espera
   * a un operador. Apagado, todo el historial, como siempre. Lo filtra y lo
   * cuenta el backend, así el paginador cuadra.
   */
  soloPorProcesar = false;

  /**
   * Redes pausadas para los clientes, de solo lectura: se encienden y apagan
   * en Horario de Operaciones. `null` si están todas activas o no se pudo leer.
   */
  redesPausadas: string | null = null;

  // Mobile accordion (id puede ser number en CL o string UUID en VES)
  expandedCardId: number | string | null = null;

  // Lock local por fila para evitar doble-click y clicks concurrentes en el mismo cliente.
  // CL: backend Lambda no valida estado en /transaccion/resend → el lock + re-fetch pre-acción
  // son la única barrera contra doble withdraw. Riesgo residual: dos operadores en máquinas
  // distintas (ver TODO backend en wallet-back/src/functions/transaction/resend.ts).
  inFlight = new Set<number | string>();

  private readonly POLLING_MS = 30_000;
  private pollingTimer: any = null;
  private visibilityHandler = () => {
    if (document.hidden) this.stopPolling();
    else this.startPolling();
  };

  constructor(
    private recipientcriptoservice: RecipientCriptoService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private currencyPipe: CurrencyPipe,
    private spinner: SpinnerService,
    private transaccionService: TransaccionService,
    private binanceWithdrawalsService: BinanceWithdrawalsAdminService,
    private mediosCripto: MediosCriptoService,
  ) {}

  async ngOnInit() {
    void this.cargarRedesPausadas();
    await this.loadTransactions();
    this.startPolling();
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /** `listar()` nunca rechaza: sin lectura no se muestra la línea. */
  private async cargarRedesPausadas(): Promise<void> {
    const estado = await this.mediosCripto.listar();
    const pausadas = REDES_CRIPTO.filter((red) => !redActiva(red.id, estado)).map((red) => red.etiqueta);
    this.redesPausadas = pausadas.length ? unirNombres(pausadas) : null;
  }

  ngOnDestroy(): void {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  private startPolling(): void {
    if (this.pollingTimer || document.hidden) return;
    this.pollingTimer = setInterval(() => {
      // Pausar refresh si hay alguna acción en curso para no pisar el flow del operador
      if (this.inFlight.size === 0) {
        this.loadTransactions();
      }
    }, this.POLLING_MS);
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  onTabChange(tab: CriptoTab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterValue = '';
    this.filtroRed = null;
    // `soloPorProcesar` se mantiene: vale para las dos pestañas.
    this.expandedCardId = null;
    this.transactionCripto = [];
    this.backTransactionCripto = [];
    this.loadTransactions();
  }

  async loadTransactions(): Promise<void> {
    const ref = this.spinner.start();
    try {
      const data = this.activeTab === 'CL'
        ? await this.recipientcriptoservice.getTransactionCripto(this.currentPage, this.pageSize, this.filtroRed, this.soloPorProcesar)
        : await this.binanceWithdrawalsService.getAsTransaccionesUsdt(this.currentPage, this.pageSize, this.soloPorProcesar);
      this.transactionCripto = data.query;
      this.backTransactionCripto = data.query;
      this.totalItems = +data.count;
      this.totalPages = Math.ceil(this.totalItems / this.pageSize);
    } finally {
      this.spinner.stop(ref);
    }
  }

  async reload(): Promise<void> {
    this.currentPage = 1;
    void this.cargarRedesPausadas();
    await this.loadTransactions();
  }

  /** Cambió el filtro de red: vuelve a la primera página, como cualquier filtro nuevo. */
  async onFiltroRedChange(red: number | null): Promise<void> {
    this.filtroRed = red ?? null;
    this.currentPage = 1;
    this.expandedCardId = null;
    await this.loadTransactions();
  }

  /** Cambió "Solo por procesar": vuelve a la primera página, igual que el filtro de red. */
  async onSoloPorProcesarChange(valor: boolean): Promise<void> {
    this.soloPorProcesar = valor;
    this.currentPage = 1;
    this.expandedCardId = null;
    await this.loadTransactions();
  }

  // Red de cada fila (pestaña Chile). Las filas sin `medioCriptoId` (backend
  // anterior, OTC) son TRC20, como en wallet-back.
  etiquetaRedFila(row: TransferenciasUSDT): string {
    return etiquetaCortaRed(row.medioCriptoId, row.redCripto);
  }

  /** No es TRC20: Binance no la paga, la paga un operador a mano. */
  esPagoManual(row: TransferenciasUSDT): boolean {
    return !this.isVesTab && !esRedBinance(row.medioCriptoId);
  }

  /** "Reenviar a Binance" solo tiene sentido para TRC20 (wallet-back rechaza las demás). */
  puedeReenviarABinance(row: TransferenciasUSDT): boolean {
    return !this.isVesTab && esRedBinance(row.medioCriptoId);
  }

  // Helpers de tab
  get isVesTab(): boolean {
    return this.activeTab === 'VES';
  }

  get montoOrigenLabel(): string {
    return this.isVesTab ? 'Monto VES' : 'Monto CLP';
  }

  get walletLabel(): string {
    // Ya no es "Wallet TRC20" en Chile: hay beneficiarios de Polygon y Solana,
    // y la red va como etiqueta en cada fila.
    return 'Wallet';
  }

  idDisplay(row: any): string {
    const id = row?.idTransferencia;
    if (id == null) return '';
    const s = String(id);
    return this.isVesTab && s.length > 8 ? s.substring(0, 8) : `#${s}`;
  }

  onPageChange(event: any) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex + 1;
    this.loadTransactions();
  }

  applyFilter() {
    if (this.filterValue === '') {
      this.transactionCripto = this.backTransactionCripto;
    }
    this.filteredTransactionCripto = this.transactionCripto.filter(transaction => {
      const transactionValues = Object.values(transaction);
      const lowerCaseFilter = this.filterValue.toLowerCase();
      return transactionValues.some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(lowerCaseFilter);
        }
        return false;
      });
    });
    this.transactionCripto = this.filteredTransactionCripto;
  }

  // Mobile accordion
  toggleCard(id: number | string): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  // Date helpers
  getDateFormatted(date: any): string {
    return date ? moment(new Date(date)).tz('America/Santiago').format('lll') : 'No aplica';
  }

  // Status helpers
  getStatusBg(row: any): string {
    const estado = row.estadoActual?.toUpperCase() || '';
    if (estado.includes('ENVIAD')) return '#16a34a';
    if (estado.includes('COMPLETAD')) return '#15803d';
    if (estado.includes('PAGAD')) return '#166534';
    if (estado.includes('RECHAZAD')) return '#dc2626';
    if (estado.includes('CANCELAD')) return '#991b1b';
    if (estado.includes('PROCESO') || estado.includes('PROCESAR')) return '#f59e0b';
    if (estado.includes('PENDIENTE')) return '#eab308';
    if (estado.includes('REVIS')) return '#8b5cf6';
    if (estado.includes('EXPIRAD')) return '#6b7280';
    return '#f3f4f6';
  }

  getStatusColor(row: any): string {
    const estado = row.estadoActual?.toUpperCase() || '';
    if (['ENVIAD', 'COMPLETAD', 'PAGAD', 'RECHAZAD', 'CANCELAD', 'PROCESO', 'PROCESAR', 'PENDIENTE', 'REVIS', 'EXPIRAD']
      .some(s => estado.includes(s))) return '#ffffff';
    return '#6b7280';
  }

  // Responsive check
  private get isMobile(): boolean {
    return window.innerWidth < 768;
  }

  isLocked(row: any): boolean {
    return this.inFlight.has(row?.idTransferencia);
  }

  // Re-fetch la página actual y devuelve el row fresco por id, o null si ya no está.
  private async fetchFreshRow(id: number | string): Promise<TransferenciasUSDT | null> {
    await this.loadTransactions();
    return this.transactionCripto.find(t => t.idTransferencia === id) ?? null;
  }

  // Guard común de acciones: lock por fila + re-fetch + validación de estado fresco.
  // Si el estado cambió (otro operador, cron, etc.), se aborta y el listado queda actualizado.
  private async withActionGuard(
    row: TransferenciasUSDT,
    expectedEstado: string,
    fn: (fresh: TransferenciasUSDT) => Promise<void>,
  ): Promise<void> {
    const id = row.idTransferencia;
    if (this.inFlight.has(id)) return;
    this.inFlight.add(id);
    try {
      const fresh = await this.fetchFreshRow(id);
      if (!fresh) {
        await Swal.fire({
          icon: 'warning',
          title: 'Transacción no disponible',
          html: `La transacción <strong>${this.idDisplay(row)}</strong> ya no está en el listado. Se actualizó la vista.`,
          confirmButtonColor: '#1C593D',
        });
        return;
      }
      if (fresh.estadoActual !== expectedEstado) {
        await Swal.fire({
          icon: 'warning',
          title: 'El estado cambió',
          html: `La transacción <strong>${this.idDisplay(row)}</strong> ya no está en <strong>${expectedEstado}</strong>.<br>Estado actual: <strong>${fresh.estadoActual}</strong>.<br><br>Se actualizó la vista para reflejar el estado real.`,
          confirmButtonColor: '#1C593D',
        });
        return;
      }
      await fn(fresh);
    } finally {
      this.inFlight.delete(id);
    }
  }

  // Detecta si el error del backend VES indica que el retiro ya fue procesado
  // (aguacate-wallet-api valida status con pessimistic_write). En ese caso
  // refrescamos el listado automáticamente.
  private isStaleStateError(err: any): boolean {
    const msg = (err?.error?.message || err?.message || (typeof err === 'string' ? err : '')) + '';
    return /ya fue procesado|no se puede procesar|estado inv[aá]lido/i.test(msg);
  }

  private extractErrorMessage(err: any, fallback: string): string {
    return err?.error?.message || err?.message || (typeof err === 'string' ? err : fallback);
  }

  async openRejectTransactionModal(row: any): Promise<void> {
    await this.withActionGuard(row, 'POR_PROCESAR', async (fresh) => {
      const dialogRef = this.dialog.open(RejectTransactionModalComponent, {
        data: fresh,
        maxHeight: '70%',
        maxWidth: this.isMobile ? '95vw' : '826px',
        minWidth: this.isMobile ? '90vw' : '800px',
      });
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (result === true) {
        await this.loadTransactions();
      }
    });
  }

  async openSendTransactionModal(row: any): Promise<void> {
    await this.withActionGuard(row, 'POR_PROCESAR', async (fresh) => {
      const dialogRef = this.dialog.open(SendTransactionaCriptoModalComponent, {
        data: fresh,
        maxHeight: '70%',
        maxWidth: this.isMobile ? '95vw' : '826px',
        minWidth: this.isMobile ? '90vw' : '800px',
      });
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (result === true) {
        await this.loadTransactions();
      }
    });
  }

  /**
   * 30/10/2026: sin botón en la plantilla. El modal guardaba por el endpoint
   * del cliente, que rechaza al operador. Queda para la edición de wallets
   * del operador, pendiente: endpoint propio, solo POR_PROCESAR, misma red y
   * registro de quién cambió qué.
   */
  async openEditModal(row: any): Promise<void> {
    await this.withActionGuard(row, 'POR_PROCESAR', async (fresh) => {
      const dialogRef = this.dialog.open(EditCriptoTransactionComponent, {
        data: fresh,
        maxHeight: '70%',
        maxWidth: this.isMobile ? '95vw' : '826px',
        minWidth: this.isMobile ? '90vw' : '800px',
      });
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (result === true) {
        await this.loadTransactions();
      }
    });
  }

  async resendTransaction(row: TransferenciasUSDT): Promise<void> {
    await this.withActionGuard(row, 'POR_PROCESAR', async (fresh) => {
      // El botón ya no se ofrece para estas filas; esto cubre una fila que
      // cambió de red entre la lista y el clic. wallet-back también la rechaza.
      if (!esRedBinance(fresh.medioCriptoId)) {
        await Swal.fire({
          icon: 'info',
          title: 'Binance no paga esta red',
          html: `La transacción <strong>${this.idDisplay(fresh)}</strong> es de la red ` +
            `<strong>${etiquetaRed(fresh.medioCriptoId, fresh.redCripto)}</strong>: se paga a mano y se marca como pagada.`,
          confirmButtonColor: '#1C593D',
        });
        return;
      }
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Reenviar transacción',
        html: `¿Reenviar la transacción <strong>${this.idDisplay(fresh)}</strong> a Binance?<br><br>` +
          `<small>Estado actual: <strong>${fresh.estadoActual}</strong><br>` +
          `Monto: <strong>${fresh.montoUSDT} USDT</strong><br>` +
          `Wallet: <code>${fresh.walletTRC20}</code></small>`,
        showCancelButton: true,
        confirmButtonColor: '#1C593D',
        cancelButtonColor: '#dc2626',
        confirmButtonText: 'Sí, reenviar',
        cancelButtonText: 'Cancelar',
      });
      if (!confirm.isConfirmed) return;

      const ref = this.spinner.start();
      try {
        await this.transaccionService.resendTransaction(Number(fresh.idTransferencia));
        this.spinner.stop(ref);
        await Swal.fire({
          icon: 'success',
          title: 'Transacción reenviada',
          text: 'La transacción fue reenviada a Binance exitosamente.',
          confirmButtonColor: '#1C593D',
        });
        await this.loadTransactions();
      } catch (error) {
        this.spinner.stop(ref);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: this.extractErrorMessage(error, 'No se pudo reenviar la transacción. Intenta nuevamente.'),
          confirmButtonColor: '#1C593D',
        });
      }
    });
  }

  /**
   * Retiro VES→USDT en POR_PROCESAR. El diálogo ofrece:
   * - Aprobar: solo cambia el estado a completado (txId de pago manual opcional).
   * - Reenviar a Binance: lo envía, previa confirmación en un segundo modal.
   * Rechazar es su propio botón.
   */
  async confirmVesWithdrawal(row: any): Promise<void> {
    await this.withActionGuard(row, 'POR_PROCESAR', async (fresh: any) => {
      const datos =
        `<small>Estado actual: <strong>${fresh.estadoActual}</strong><br>` +
        `Monto: <strong>${fresh.montoUSDT} USDT</strong><br>` +
        `Wallet: <code>${fresh.walletTRC20}</code></small>`;

      const eleccion = await Swal.fire({
        icon: 'question',
        title: 'Atender retiro USDT',
        html: `Retiro <strong>${this.idDisplay(fresh)}</strong>.<br>` +
          `<b>Aprobar</b> solo cambia el estado a completado, sin enviar nada a Binance. ` +
          `<b>Reenviar a Binance</b> lo envía.<br><br>` + datos,
        input: 'text',
        inputLabel: 'TxId del pago manual (opcional, solo para Aprobar)',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: '#1C593D',
        denyButtonColor: '#2563eb',
        cancelButtonColor: '#dc2626',
        confirmButtonText: 'Aprobar',
        denyButtonText: 'Reenviar a Binance',
        cancelButtonText: 'Cancelar',
      });
      if (!eleccion.isConfirmed && !eleccion.isDenied) return;

      const reenviar = eleccion.isDenied;
      if (reenviar) {
        const ok = await Swal.fire({
          icon: 'warning',
          title: 'Reenviar a Binance',
          html: `Se enviará el retiro <strong>${this.idDisplay(fresh)}</strong> a Binance y quedará en proceso hasta que responda.<br>No se deshace. No lo envíes si ya lo pagaste a mano.<br><br>` + datos,
          showCancelButton: true,
          confirmButtonColor: '#1C593D',
          cancelButtonColor: '#dc2626',
          confirmButtonText: 'Sí, reenviar',
          cancelButtonText: 'Cancelar',
        });
        if (!ok.isConfirmed) return;
      }
      const txId = !reenviar && typeof eleccion.value === 'string' ? eleccion.value.trim() : '';

      const ref = this.spinner.start();
      try {
        if (reenviar) {
          await this.binanceWithdrawalsService.resendWithdrawal(String(fresh.idTransferencia));
        } else {
          await this.binanceWithdrawalsService.confirmWithdrawal(String(fresh.idTransferencia), txId || undefined);
        }
        this.spinner.stop(ref);
        await Swal.fire({
          icon: 'success',
          title: reenviar ? 'Retiro reenviado' : 'Retiro aprobado',
          text: reenviar ? 'El retiro se envió a Binance.' : 'El retiro quedó aprobado.',
          confirmButtonColor: '#1C593D',
        });
        await this.loadTransactions();
      } catch (err: any) {
        this.spinner.stop(ref);
        const stale = this.isStaleStateError(err);
        await Swal.fire({
          icon: stale ? 'warning' : 'error',
          title: stale ? 'El estado cambió' : 'Error',
          text: this.extractErrorMessage(err, 'No se pudo atender el retiro.'),
          confirmButtonColor: '#1C593D',
        });
        if (stale) await this.loadTransactions();
      }
    });
  }

  async rejectVesWithdrawal(row: any): Promise<void> {
    await this.withActionGuard(row, 'POR_PROCESAR', async (fresh) => {
      const { value: reason, isConfirmed } = await Swal.fire({
        icon: 'warning',
        title: 'Rechazar retiro',
        html: `Rechazar el retiro <strong>${this.idDisplay(fresh)}</strong>. El saldo VES se devolverá al usuario.<br><br>` +
          `<small>Estado actual: <strong>${fresh.estadoActual}</strong><br>` +
          `Monto: <strong>${fresh.montoUSDT} USDT</strong></small>`,
        input: 'textarea',
        inputLabel: 'Motivo del rechazo',
        inputPlaceholder: 'Escribí la razón...',
        inputValidator: (v) => (!v || v.trim().length < 5 ? 'Ingresá un motivo (mínimo 5 caracteres)' : null),
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#1C593D',
        confirmButtonText: 'Sí, rechazar',
        cancelButtonText: 'Cancelar',
      });
      if (!isConfirmed || !reason) return;

      const ref = this.spinner.start();
      try {
        await this.binanceWithdrawalsService.rejectWithdrawal(String(fresh.idTransferencia), reason);
        this.spinner.stop(ref);
        await Swal.fire({
          icon: 'success',
          title: 'Retiro rechazado',
          text: 'El retiro fue rechazado y el saldo VES revertido.',
          confirmButtonColor: '#1C593D',
        });
        await this.loadTransactions();
      } catch (err: any) {
        this.spinner.stop(ref);
        const stale = this.isStaleStateError(err);
        await Swal.fire({
          icon: stale ? 'warning' : 'error',
          title: stale ? 'El estado cambió' : 'Error al rechazar',
          text: this.extractErrorMessage(err, 'No se pudo rechazar el retiro.'),
          confirmButtonColor: '#1C593D',
        });
        if (stale) await this.loadTransactions();
      }
    });
  }

  /**
   * Detalle de solo lectura de una transacción, en cualquier estado. Las
   * enviadas y las que están en Binance solo tienen esta opción (y copiar);
   * las acciones quedan para POR_PROCESAR.
   */
  verDetalle(row: any): void {
    const esc = (v: any) =>
      String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c]);
    const fecha = (v: any) => (v ? new Date(v).toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : '');
    const filas: Array<[string, any]> = [
      ['ID', this.idDisplay(row)],
      ['Estado', row.estadoActual],
      ['Fecha de inicio', fecha(row.fechaInicio)],
      ['Fondeo de la wallet', fecha(row.fechaFondeoWallet ?? row.fechaFondoWallet)],
      ['Fecha de término', fecha(row.fechaTermino)],
      ['Remitente', row.remitente],
      ['Beneficiario', row.beneficiario],
      ['Monto origen', row.montoCLP],
      ['Tasa', row.tn],
      ['Monto USDT', row.montoUSDT],
      ['Wallet', row.walletTRC20],
      // VES: la red del retiro (Nest). Chile: la del beneficiario, y si no la
      // paga Binance se dice, porque es lo que decide cómo se atiende.
      ['Red', this.isVesTab
        ? row.network
        : etiquetaRed(row.medioCriptoId, row.redCripto) + (this.esPagoManual(row) ? ' (pago manual)' : '')],
      ['TxId / comentario', row.comentarios],
      ['Referencia Binance', row.referencia],
    ];
    const html =
      '<table style="width:100%;text-align:left;font-size:0.85rem;border-collapse:collapse">' +
      filas
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(
          ([k, v]) =>
            `<tr><td style="padding:4px 8px;color:#6b7280;white-space:nowrap;vertical-align:top">${esc(k)}</td>` +
            `<td style="padding:4px 8px;word-break:break-all">${esc(v)}</td></tr>`,
        )
        .join('') +
      '</table>';

    Swal.fire({
      title: 'Detalle de la transacción',
      html,
      width: 640,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#1C593D',
    });
  }

  copy(element: TransferenciasUSDT) {
    var text = '';
    // La red va en su propia línea: con Polygon y Solana, "Wallet TRC20" haría
    // pagar por Tron una dirección de otra red.
    const red = this.isVesTab
      ? element.network || 'TRC20'
      : etiquetaRed(element.medioCriptoId, element.redCripto);
    if (element.estadoActual === 'POR_PROCESAR') {
      text =
        `Beneficiario: ${element.beneficiario}\nRemitente: ${element.remitente}\nFecha Inicio: ${element.fechaInicio}\nTasa Negociación: ${element.tn}\nMonto CLP: ${element.montoCLP}\nMonto USDT: ${element.montoUSDT}\nWallet: ${element.walletTRC20}\nRed: ${red}`;
    } else {
      text =
        `Beneficiario: ${element.beneficiario}\nRemitente: ${element.remitente}\nFecha Inicio: ${element.fechaInicio}\nFecha Termino: ${element.fechaTermino}\nTasa Negociación: ${element.tn}\nMonto CLP: ${element.montoCLP}\nMonto USDT: ${element.montoUSDT}\nWallet: ${element.walletTRC20}\nRed: ${red}`;
    }
    this.clipboard.copy(text);
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
    Toast.fire({
      icon: 'success',
      title: 'Transferencia copiada al portapapeles'
    });
  }
}
